import { blogMediaUrl } from "./blogMedia";

/** True if body is HTML from the rich editor (vs legacy plain text). */
export function plainTextToHtml(text) {
  if (!text?.trim()) return "";
  return text
    .split(/\n\n+/)
    .map((block) => `<p>${block.trim().replace(/\n/g, "<br>")}</p>`)
    .join("");
}

export function isHtmlBlogBody(body) {
  const t = (body || "").trim();
  if (!t) return false;
  if (t.startsWith("<")) return true;
  return /<(p|h[1-6]|ul|ol|li|blockquote|figure|video|img|br)\b/i.test(t);
}

const ALLOWED_TAGS = new Set([
  "P", "BR", "STRONG", "B", "EM", "I", "U", "S",
  "UL", "OL", "LI", "H1", "H2", "H3", "H4", "H5", "H6",
  "BLOCKQUOTE", "A", "IMG", "VIDEO", "SOURCE", "FIGURE", "FIGCAPTION",
  "DIV", "SPAN", "HR", "PRE", "CODE",
]);

const ALLOWED_ATTR = {
  A: new Set(["href", "title", "rel", "target"]),
  IMG: new Set(["src", "alt", "width", "height", "loading", "crossorigin", "referrerpolicy"]),
  VIDEO: new Set(["src", "controls", "playsinline", "preload", "width", "height", "poster", "crossorigin"]),
  SOURCE: new Set(["src", "type"]),
  "*": new Set(["class"]),
};

const SAFE_URI = /^(https?:|\/api\/cms\/blog\/media\/|data:image\/(png|jpeg|gif|webp);base64,)/i;

function attrAllowed(tag, name, value) {
  const n = name.toLowerCase();
  if (n.startsWith("on") || n === "style" || n === "srcdoc") return false;
  const allowed = new Set([...(ALLOWED_ATTR[tag] || []), ...ALLOWED_ATTR["*"]]);
  if (!allowed.has(n)) return false;
  if (n === "href" || n === "src" || n === "poster") {
    const v = (value || "").trim();
    if (!v || /^(javascript|vbscript|data:text)/i.test(v)) return false;
    if (!SAFE_URI.test(v)) return false;
  }
  return true;
}

/** Allowlist-sanitize HTML in the browser (no extra npm package). */
export function sanitizeBlogHtml(raw) {
  if (!raw) return "";
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return String(raw).replace(/<(script|iframe|object|embed|form|marquee|svg|style)\b[^>]*>[\s\S]*?<\/\1>/gi, "");
  }
  const doc = new DOMParser().parseFromString(`<div id="root">${raw}</div>`, "text/html");
  const root = doc.getElementById("root");
  if (!root) return "";

  const walk = (node) => {
    const kids = Array.from(node.childNodes);
    for (const child of kids) {
      if (child.nodeType === 1) {
        const tag = child.tagName;
        if (!ALLOWED_TAGS.has(tag)) {
          child.remove();
          continue;
        }
        for (const attr of Array.from(child.attributes)) {
          if (!attrAllowed(tag, attr.name, attr.value)) {
            child.removeAttribute(attr.name);
          }
        }
        if (tag === "IMG" || tag === "VIDEO") {
          child.setAttribute("crossorigin", "anonymous");
        }
        walk(child);
      }
    }
  };
  walk(root);
  return root.innerHTML;
}

/** Rewrite relative media paths + sanitize HTML for public display (XSS-safe). */
export function resolveBlogBodyHtml(html) {
  if (!html) return "";
  let out = html.replace(
    /(src|href)=(["'])(\/api\/cms\/blog\/media\/[^"']+)\2/gi,
    (_, attr, quote, path) => `${attr}=${quote}${blogMediaUrl(path)}${quote}`,
  );
  out = out.replace(
    /(src|href)=(["'])https?:\/\/[^"']+(\/api\/cms\/blog\/media\/[^"']+)\2/gi,
    (_, attr, quote, path) => `${attr}=${quote}${blogMediaUrl(path)}${quote}`,
  );
  return sanitizeBlogHtml(out);
}

/** Normalize API post → form state (never null on controlled inputs). */
export function normalizeBlogForm(data, emptyForm) {
  const base = { ...emptyForm };
  if (!data) return base;

  const rawBody = data.body ?? "";
  const body =
    rawBody && !isHtmlBlogBody(rawBody) ? plainTextToHtml(rawBody) : String(rawBody);

  return {
    ...base,
    ...data,
    title: data.title ?? "",
    slug: data.slug ?? "",
    excerpt: data.excerpt ?? "",
    body,
    thumbnail_url: data.thumbnail_url ?? "",
    status: data.status === "published" ? "published" : "draft",
    author_name: data.author_name ?? "",
  };
}

export function blogBodyHasContent(body) {
  if (!body?.trim()) return false;
  if (isHtmlBlogBody(body)) {
    const text = body.replace(/<[^>]+>/g, "").trim();
    if (text) return true;
    return /<(img|video)\s/i.test(body);
  }
  return body.trim().length > 0;
}
